// ════════════════════════════════════════════════════════════════════
// NodePalette — left sidebar of draggable node types. Drag a tile onto
// the canvas and the editor catches the drop, calls apiAddWorkflowStep,
// and positions the new node at the cursor.
// ════════════════════════════════════════════════════════════════════
import type { WorkflowStepType } from "@/lib/api";
import { STEP_META, tintClasses } from "./nodes";

const PALETTE_ITEMS: { type: WorkflowStepType; description: string }[] = [
  { type: "approval",       description: "Pauses for an approver; emits one outgoing edge." },
  { type: "task",           description: "Assigns work to a user; emits one outgoing edge." },
  { type: "service_action", description: "Runs a built-in platform action automatically." },
  { type: "notification",   description: "Sends an in-app notification or email." },
  { type: "condition",      description: "Routes by an expression (true / false outputs)." },
  { type: "switch",         description: "Routes by a field value (N case outputs)." },
  { type: "parallel",       description: "Fans out to N concurrent branches." },
  { type: "merge",          description: "Waits for N inbound branches, then continues." },
  { type: "delay",          description: "Pauses the workflow for a configurable period." },
  { type: "end",            description: "Explicit terminator; the workflow completes here." },
];

export const PALETTE_DRAG_MIME = "application/x-bhrapp-workflow-node";

export function NodePalette() {
  return (
    <aside className="w-56 shrink-0 border-r border-border bg-muted/30 flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border bg-card/60">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          Drag to canvas
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {PALETTE_ITEMS.map((it) => {
          const meta = STEP_META[it.type];
          const t = tintClasses(meta.tint);
          const Icon = meta.icon;
          return (
            <div
              key={it.type}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(PALETTE_DRAG_MIME, it.type);
                e.dataTransfer.effectAllowed = "copy";
              }}
              className={`group rounded-lg border ${t.border} bg-card hover:bg-muted/50 cursor-grab active:cursor-grabbing p-2.5 transition-colors`}
              title={it.description}
            >
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded ${t.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-3.5 h-3.5 ${t.text}`} />
                </div>
                <span className="text-xs font-medium text-foreground">{meta.label}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug line-clamp-2">
                {it.description}
              </p>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
